import { Helmet } from 'react-helmet-async'

/**
 * Adds Schema.org structured data for Organization and WebSite
 * This helps search engines understand your business and website structure
 */
export function OrganizationSchema() {
    const schemaData = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'Organization',
                '@id': 'https://makemerry.app/#organization',
                name: 'MakeMerry',
                url: 'https://makemerry.app',
                logo: {
                    '@type': 'ImageObject',
                    url: 'https://makemerry.app/logo.png'
                },
                description: 'Discover local events, activities, and fun places in your area. MakeMerry helps you find and plan exciting experiences.',
                contactPoint: {
                    '@type': 'ContactPoint',
                    email: 'support@makemerry.app',
                    contactType: 'Customer Support'
                }
            },
            {
                '@type': 'WebSite',
                '@id': 'https://makemerry.app/#website',
                url: 'https://makemerry.app',
                name: 'MakeMerry',
                description: 'Search for events and places by date and location',
                publisher: {
                    '@id': 'https://makemerry.app/#organization'
                },
                potentialAction: {
                    '@type': 'SearchAction',
                    target: {
                        '@type': 'EntryPoint',
                        urlTemplate: 'https://makemerry.app/?date={search_term_string}'
                    },
                    'query-input': 'required name=search_term_string'
                }
            }
        ]
    }

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(schemaData)}
            </script>
        </Helmet>
    )
}

/**
 * Adds Schema.org structured data for an Event
 */
export function EventSchema({ event }) {
    if (!event) return null

    const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: event.name || event.title,
        description: event.description || event.summary,
        startDate: event.start_time || event.startTime,
        endDate: event.end_time || event.endTime,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: event.virtual ? 'https://schema.org/OnlineEventAttendanceMode' : 'https://schema.org/OfflineEventAttendanceMode',
        location: event.location ? {
            '@type': event.virtual ? 'VirtualLocation' : 'Place',
            name: event.location.name || event.location_name,
            address: event.location.address ? {
                '@type': 'PostalAddress',
                streetAddress: event.location.address.street || event.location.address,
                addressLocality: event.location.address.city || event.location.city,
                addressRegion: event.location.address.state || event.location.state,
                postalCode: event.location.address.zip || event.location.zip,
                addressCountry: event.location.address.country || event.location.country || 'US'
            } : undefined,
            geo: (event.location.latitude || event.lat) && (event.location.longitude || event.lon) ? {
                '@type': 'GeoCoordinates',
                latitude: event.location.latitude || event.lat,
                longitude: event.location.longitude || event.lon
            } : undefined
        } : undefined,
        image: event.image_url || event.image || event.thumbnail,
        url: event.url || `https://makemerry.app/event/${event.id}`,
        organizer: event.organizer ? {
            '@type': 'Organization',
            name: event.organizer.name || event.organizer,
            url: event.organizer.url
        } : undefined,
        offers: event.price || event.ticket_url ? {
            '@type': 'Offer',
            url: event.ticket_url || event.url,
            price: event.price || '0',
            priceCurrency: event.currency || 'USD',
            availability: 'https://schema.org/InStock',
            validFrom: new Date().toISOString()
        } : undefined
    }

    // Remove undefined fields
    Object.keys(schemaData).forEach(key =>
        schemaData[key] === undefined && delete schemaData[key]
    )

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(schemaData)}
            </script>
        </Helmet>
    )
}

/**
 * Adds Schema.org structured data for a Place/LocalBusiness
 */
export function PlaceSchema({ place }) {
    if (!place) return null

    // Determine the most specific type based on place category
    let schemaType = 'Place'
    const category = place.category?.toLowerCase()

    if (category) {
        const typeMapping = {
            'restaurant': 'Restaurant',
            'bar': 'BarOrPub',
            'cafe': 'CafeOrCoffeeShop',
            'hotel': 'Hotel',
            'museum': 'Museum',
            'park': 'Park',
            'theater': 'TheaterEvent',
            'shopping': 'ShoppingCenter',
            'gym': 'SportsActivityLocation',
            'entertainment': 'EntertainmentBusiness'
        }
        schemaType = typeMapping[category] || 'LocalBusiness'
    }

    const schemaData = {
        '@context': 'https://schema.org',
        '@type': schemaType,
        name: place.name || place.title,
        description: place.description || place.summary,
        address: place.address ? {
            '@type': 'PostalAddress',
            streetAddress: place.address.street || place.address,
            addressLocality: place.address.city || place.city,
            addressRegion: place.address.state || place.state,
            postalCode: place.address.zip || place.zip,
            addressCountry: place.address.country || place.country || 'US'
        } : undefined,
        geo: (place.latitude || place.lat) && (place.longitude || place.lon) ? {
            '@type': 'GeoCoordinates',
            latitude: place.latitude || place.lat,
            longitude: place.longitude || place.lon
        } : undefined,
        image: place.image_url || place.image || place.thumbnail,
        url: place.website || place.url || `https://makemerry.app/place/${place.id}`,
        telephone: place.phone || place.telephone,
        priceRange: place.price_range || place.priceRange,
        openingHoursSpecification: place.hours ? parseOpeningHours(place.hours) : undefined,
        aggregateRating: place.rating ? {
            '@type': 'AggregateRating',
            ratingValue: place.rating,
            bestRating: place.rating_scale || '5',
            reviewCount: place.review_count || place.reviews
        } : undefined
    }

    // Remove undefined fields
    Object.keys(schemaData).forEach(key =>
        schemaData[key] === undefined && delete schemaData[key]
    )

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(schemaData)}
            </script>
        </Helmet>
    )
}

/**
 * Helper function to parse opening hours into Schema.org format
 * Expects hours in format like: { "monday": "9:00-17:00", ... }
 */
function parseOpeningHours(hours) {
    if (!hours || typeof hours !== 'object') return undefined

    const dayMapping = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
    }

    return Object.entries(hours).map(([day, timeRange]) => {
        const [opens, closes] = timeRange.split('-')
        return {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: dayMapping[day.toLowerCase()] || day,
            opens: opens?.trim(),
            closes: closes?.trim()
        }
    })
}
